/**
 * A persistent profile's browser state (cookies, storage, history, open tabs,
 * logins, extensions): the worker's user-data-dir, pruned of caches, archived,
 * encrypted, and kept in object storage between sessions.
 *
 * The worker restores it before Chromium starts and saves it after Chromium
 * exits. The cloud only hands out signed URLs and the per-profile key; it never
 * sees the archive's contents.
 */

import { z } from "zod";

const Sha256Schema = z
	.string()
	.regex(/^[a-f0-9]{64}$/)
	.describe("Lowercase hex SHA-256 of the encrypted archive");

const StateKeySchema = z
	.string()
	.min(1)
	.describe("Base64 AES-256-GCM key the archive is encrypted with");

/** In the launch config: where the latest saved state is and how to open it. */
export const ProfileStateDownloadSchema = z
	.object({
		version: z.number().int().min(1).describe("Committed state version"),
		downloadUrl: z.string().url().describe("Short-lived signed GET URL"),
		key: StateKeySchema,
		sha256: Sha256Schema,
		size: z.number().int().min(0).describe("Encrypted archive size in bytes"),
		browserMajor: z
			.number()
			.int()
			.nullable()
			.describe(
				"Chromium major that wrote the state. Chromium will not open a profile written by a newer major.",
			),
	})
	.describe("Saved browser state to restore at launch");

export type ProfileStateDownload = z.infer<typeof ProfileStateDownloadSchema>;

/** `POST /v1/profiles/:id/state/upload-url` */
export const ProfileStateUploadRequestSchema = z
	.object({
		sessionId: z
			.string()
			.min(1)
			.max(128)
			.describe("Local API session UUID that produced the state"),
	})
	.describe("Request an upload URL for a session's browser state");

export const ProfileStateUploadSchema = z
	.object({
		version: z.number().int().min(1).describe("Version to commit on success"),
		uploadUrl: z.string().url().describe("Short-lived signed PUT URL"),
		key: StateKeySchema,
		maxBytes: z
			.number()
			.int()
			.min(1)
			.describe("Largest encrypted archive the cloud accepts"),
		expiresAt: z.string().describe("When the upload URL stops working"),
	})
	.describe("Where to upload a session's browser state");

export type ProfileStateUpload = z.infer<typeof ProfileStateUploadSchema>;

/** `POST /v1/profiles/:id/state/commit` */
export const ProfileStateCommitSchema = z
	.object({
		sessionId: z.string().min(1).max(128).describe("Local API session UUID"),
		version: z.number().int().min(1).describe("Version from upload-url"),
		sha256: Sha256Schema,
		size: z.number().int().min(1).describe("Encrypted archive size in bytes"),
		browserMajor: z
			.number()
			.int()
			.min(1)
			.optional()
			.describe("Chromium major the session ran"),
	})
	.describe("Make an uploaded state the profile's current one");

export type ProfileStateCommit = z.infer<typeof ProfileStateCommitSchema>;

export const ProfileStateCommitResponseSchema = z
	.object({
		version: z.number().int().min(1),
		savedAt: z.string(),
	})
	.describe("Committed browser state");

/** What the dashboard shows about a profile's saved browser state. */
export const ProfileBrowserStateSchema = z
	.object({
		version: z.number().int().min(1),
		size: z.number().int().min(0).describe("Encrypted archive size in bytes"),
		savedAt: z.string().describe("When the last session's state was saved"),
	})
	.describe("Saved browser state");

export type ProfileBrowserState = z.infer<typeof ProfileBrowserStateSchema>;
